import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = {
  key: 2,
  class: "no-data"
}
const _hoisted_2 = {
  "data-int": "collections-no-data",
  class: "subtitle has-text-centered py-4"
}

import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'

import { usePartCollectionColumns } from '@/composables/collections/usePartCollectionColumns'
import { usePartCollectionSearch } from '@/composables/collections/usePartCollectionSearch'
import { usePartCollectionsData } from '@/composables/collections/usePartCollectionsData'
import { useSearchCollectionHydration } from '@/composables/collections/useSearchCollectionHydration'
import { useSelectedCollectionScroll } from '@/composables/collections/useSelectedCollectionScroll'
import { createNormalizePart } from '@/composables/collections/createNormalizePart'
import useCart from '@/composables/useCart'
import { getColumnTr } from '@/helpers/part'
import { getPartsAsXlsx } from '@/controllers/library/productPage'
import { downloadXlsxFrom } from '@/helpers/fileDownload'
import useDetailPage from '../details/useDetailPage'
import { useBreakpoints } from "@/helpers"
import MobilePartCollectionsLayout from './MobilePartCollectionsLayout.vue'

import type {
  DisplayCollection,
  SearchResultPart,
  PartRow
} from '@/interfaces/library/partCollection'

import PartCollectionsLayout from './PartCollectionsLayout.vue'

type LayoutExposed = {
  scrollRef: HTMLElement | null
}

const COLLAPSED_ESTIMATE = 48
const EXPANDED_BASE_ESTIMATE = 114
const PART_ROW_ESTIMATE = 57

// --------------------------------------------------
// Base state / source composables
// --------------------------------------------------

export default /*#__PURE__*/_defineComponent({
  __name: 'PartCollectionsWrapper',
  setup(__props) {

const { width } = useBreakpoints()
const isMobile = computed(() => width.value <= 768)

const store = useStore()
const { hasPartCollections, content } = useDetailPage()
const id = computed(() => content.value?.id)
const isWidget = computed(() => {
  return store.state.auth.isWidget
})

const route = useRoute()

const {
  cartItem,
  addToCartModalOpen,
  isCartOrErpCartEnabled,
  isWidgetCartEnabled,
  canOrderPart,
  closeAddToCartModal,
  addToCart,
  addPartsToCart,
  handleCartModalSubmit
} = useCart()

const normalizePart = createNormalizePart(canOrderPart)

const {
  collections,
  expandedIds,
  isExpandingAll,
  collectionIdToPageIdMap,
  selectedCollectionId,
  hasMoreCollections,
  isLoadingCollections,
  isLoadingMoreCollections,
  hasAnExpandedCollection,
  loadCollections,
  loadMoreCollections,
  loadPartsForCollection,
  toggleCollection,
  expandAll,
  collapseAll,
  resetIfOwner,
  toggleShowRetailPrice,
  toggleShowWholesalePrice,
  toggleShowDiscountPrice
} = usePartCollectionsData(
  id.value,
  normalizePart
)

const {
  search,
  searchResults,
  isSearching,
  isSearchMode,
  onSearchInput,
  clearSearch,
  resetSearchContext,
  runSearch
} = usePartCollectionSearch(
  id.value,
  normalizePart
)

const {
  effectivePartColumns,
  columnWidths,
  contentWidth,
  loadPartColumns
} = usePartCollectionColumns()

const layoutRef = ref<LayoutExposed | null>(null)
const scrollRef = ref<HTMLElement | null>(null)

watch(
  layoutRef,
  value => {
    scrollRef.value = value?.scrollRef ?? null
  },
  { immediate: true }
)

// --------------------------------------------------
// Display computeds
// --------------------------------------------------
const totalCount = computed(() => content.value?.partCollectionsCount ?? 0)
const hasHidePrice = computed(() => store.getters['user/hasHidePrice'])

const searchCollections = computed<DisplayCollection[]>(() => {
  const grouped = new Map<number, PartRow[]>()

  for (const part of searchResults.value as Array<SearchResultPart & PartRow>) {
    for (const collectionId of part.partCollectionIds ?? []) {
      const existing = grouped.get(collectionId)
      const pageId = collectionIdToPageIdMap.value.get(collectionId)
      const updatedPart = { ...part }

      if (pageId != null) {
        updatedPart.pageId = pageId
      }

      if (existing) {
        existing.push(updatedPart)
      } else {
        grouped.set(collectionId, [updatedPart])
      }
    }
  }

  return collections.value.map(collection => {
    const matchedParts = grouped.get(collection.id) ?? []
    const hasMatch = matchedParts.length > 0

    return {
      rowKey: `collection-${collection.id}`,
      id: collection.id,
      pageId: collection.pageId ?? null,
      name: collection.name,
      description: collection.description ?? '',
      partCount: collection.partCount ?? collection.parts?.length ?? 0,
      isSearchResult: hasMatch,
      parts: hasMatch ? matchedParts : [],
      partsLoaded: hasMatch,
      partsLoading: false,
      partsBulkLoading: false,
      showRetail: collection.showRetail,
      showWholesale: collection.showWholesale,
      showDiscount: collection.showDiscount
    }
  })
})

const missingSearchCollectionIds = computed<number[]>(() => {
  const ids = new Set<number>()

  for (const part of searchResults.value as Array<SearchResultPart & PartRow>) {
    for (const collectionId of part.partCollectionIds ?? []) {
      if (!collectionIdToPageIdMap.value.has(collectionId)) {
        ids.add(collectionId)
      }
    }
  }

  return [...ids]
})

const normalCollections = computed<DisplayCollection[]>(() =>
  collections.value.map(collection => ({
    rowKey: `collection-${collection.id}`,
    id: collection.id,
    pageId: collection.pageId ?? null,
    name: collection.name,
    description: collection.description ?? '',
    partCount: collection.partCount ?? collection.parts?.length ?? 0,
    parts: (collection.parts ?? []) as PartRow[],
    partsLoaded: !!collection.partsLoaded,
    partsLoading: !!collection.partsLoading,
    partsBulkLoading: !!collection.partsBulkLoading,
    showRetail: collection.showRetail,
    showWholesale: collection.showWholesale,
    showDiscount: collection.showDiscount
  }))
)

const visibleCollections = computed<DisplayCollection[]>(() =>
  isSearchMode.value ? searchCollections.value : normalCollections.value
)

const displayedSearchResultsCount = computed<number>(() => {
  if (!isSearchMode.value) return 0
  // Count total part rows that will be displayed across all collections
  // This gives accurate count immediately, before collection hydration completes
  let count = 0
  for (const part of searchResults.value as Array<SearchResultPart & PartRow>) {
    count += (part.partCollectionIds ?? []).length
  }
  return count
})

function getExpanded(collection: DisplayCollection): boolean {
  return isSearchMode.value
    ? collection.parts.length > 0
    : expandedIds.value.has(collection.id)
}

function estimatedHeightForCollection(collection: DisplayCollection): number {
  if (getExpanded(collection)) {
    return EXPANDED_BASE_ESTIMATE + (collection.parts.length * PART_ROW_ESTIMATE)
  }

  return COLLAPSED_ESTIMATE
}

// --------------------------------------------------
// Scroll helpers (no outer virtual list)
// --------------------------------------------------

function resetScroll() {
  if (scrollRef.value) {
    scrollRef.value.scrollTop = 0
  }
}

function setScrollTop(top: number) {
  const nextTop = Math.max(0, top)

  if (scrollRef.value) {
    scrollRef.value.scrollTop = nextTop
  }
}

function getItemElement(id: number): HTMLElement | null {
  return document.getElementById(`part-collection-${id}`)
}

// --------------------------------------------------
// User actions
// --------------------------------------------------

function getPartLocation(_part: PartRow) {
  // If we want the location to show up in cart we can
  // use this in the future
  return {}
}

async function exportParts(collectionId: number) {
  const columnTrMap = effectivePartColumns.value.reduce((obj, col) => {
    obj[col] = getColumnTr(col)
    return obj
  }, {} as Record<string, string>)

  const response = await getPartsAsXlsx(id.value, collectionId, columnTrMap)
  const jsonText = await response.data.text()
  const payload = JSON.parse(jsonText)
  downloadXlsxFrom(payload.body, 'parts.xlsx')
}

function isNearBottom(event: Event, threshold = 160): boolean {
  const target = event.target as HTMLElement | null
  if (!target) return false

  return target.scrollTop + target.clientHeight >= target.scrollHeight - threshold
}

async function handleScroll(event: Event) {
  if (isSearchMode.value) return
  if (isLoadingCollections.value) return
  if (isLoadingMoreCollections.value) return
  if (!hasMoreCollections.value) return
  if (!isNearBottom(event)) return

  await loadMoreCollections()
}

async function handleMobileLoadMore() {
  if (isLoadingMoreCollections.value) return
  await loadMoreCollections()
}
async function handleMobileScrollToView(id: number) {
  document.getElementById(`part-collection-${id}`)
    ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}
async function handleDesktopScrollToView(id: number) {
  document.getElementById('PartCollections')?.scrollIntoView()

  if (scrollRef.value) {
    const el = getItemElement(id)
    if (el) {
      const offset = el.getBoundingClientRect().top
        - scrollRef.value.getBoundingClientRect().top
        + scrollRef.value.scrollTop
      setScrollTop(offset)
    }
  }
}
function getFirstMatchingId() {
  const firstMatch = visibleCollections.value.find(c => c.parts.length > 0)
  return firstMatch?.id ?? null
}
async function scrollToView(fun: Function) {
  const stopWatch = watch(searchResults, async () => {
    stopWatch()
    await nextTick()
    const id = getFirstMatchingId()
    if (!!id) {
      fun(id)
    }
  })
}

function handleClearSearch() {
  clearSearch(resetScroll)
  collapseAll(false)
}

function handleExpandAll() {
  expandAll(isSearchMode.value)
}

function handleCollapseAll() {
  collapseAll(isSearchMode.value)
}

function handleToggleCollection(collectionId: number) {
  toggleCollection(collectionId, isSearchMode.value)
}

function handleSearchInput(event: Event) {
  onSearchInput(event, resetScroll)
}

function handleAddToCart(payload: PartRow) {
  addToCart(payload, getPartLocation)
}

function handleAddAllToCart(payload: PartRow[]) {
  addPartsToCart(payload, getPartLocation)
}

function handleCartSubmit(qty: number) {
  handleCartModalSubmit(qty, getPartLocation)
}

// --------------------------------------------------
// Support Both Usage in this component & AnchorNavigation.vue dropdown
// Selecting a collection in anchor tag dropdown should scroll to it.
// --------------------------------------------------

useSearchCollectionHydration({
  isSearchMode,
  isLoadingCollections,
  isLoadingMoreCollections,
  hasMoreCollections,
  missingSearchCollectionIds,
  loadMoreCollections
})

useSelectedCollectionScroll({
  selectedCollectionId,
  isSearchMode,
  collections,
  visibleCollections,
  expandedIds,
  hasMoreCollections,
  isLoadingMoreCollections,
  estimatedHeightForCollection,
  collapseAll,
  loadMoreCollections,
  loadPartsForCollection,
  setScrollTop,
  getItemElement,
  scrollRef
})

// --------------------------------------------------
// Lifecycle
// --------------------------------------------------

onMounted(async () => {
  store.dispatch('user/initializePricingVisibility')
  // Clear stored query immediately to prevent cross-page leakage
  const storedQuery = store.state.search?.filters?.partCollectionQuery
  store.dispatch('search/clearPartCollectionQuery')

  if (hasPartCollections.value) {
    await Promise.all([loadPartColumns(), loadCollections()])

    const rawTerm = storedQuery || (route.query.partNumber ? String(route.query.partNumber) : null)
    if (rawTerm) {
      search.value = rawTerm
      runSearch(rawTerm)
      if (isMobile.value) {
        await scrollToView(handleMobileScrollToView)
      } else {
        await scrollToView(handleDesktopScrollToView)
      }
    }
  }
})

onBeforeUnmount(() => {
  resetSearchContext()
  resetIfOwner()
})

return (_ctx: any,_cache: any) => {
  return (_unref(hasPartCollections) && isMobile.value)
    ? (_openBlock(), _createBlock(MobilePartCollectionsLayout, {
        key: 0,
        search: _unref(search),
        "search-results-count": displayedSearchResultsCount.value,
        "is-searching": _unref(isSearching),
        "is-search-mode": _unref(isSearchMode),
        "is-expanding-all": _unref(isExpandingAll),
        collections: visibleCollections.value,
        "column-widths": _unref(columnWidths),
        "content-width": _unref(contentWidth),
        "effective-part-columns": _unref(effectivePartColumns),
        "is-cart-or-erp-cart-enabled": _unref(isCartOrErpCartEnabled),
        "add-to-cart-modal-open": _unref(addToCartModalOpen),
        "cart-item": _unref(cartItem),
        "is-widget-cart-enabled": _unref(isWidgetCartEnabled),
        "get-expanded": getExpanded,
        "has-hide-price": hasHidePrice.value,
        "has-more-collections": _unref(hasMoreCollections),
        "total-count": totalCount.value,
        "is-widget": isWidget.value,
        onExpandAll: handleExpandAll,
        onCollapseAll: handleCollapseAll,
        onSearchInput: handleSearchInput,
        onClearSearch: handleClearSearch,
        onToggleCollection: handleToggleCollection,
        onAddToCart: handleAddToCart,
        onAddAllToCart: handleAddAllToCart,
        onExportParts: exportParts,
        onCartSubmit: handleCartSubmit,
        onCloseCartModal: _unref(closeAddToCartModal),
        onToggleShowRetailPrice: _unref(toggleShowRetailPrice),
        onToggleShowWholesalePrice: _unref(toggleShowWholesalePrice),
        onToggleShowDiscountPrice: _unref(toggleShowDiscountPrice),
        onLoadMore: handleMobileLoadMore
      }, null, 8, ["search", "search-results-count", "is-searching", "is-search-mode", "is-expanding-all", "collections", "column-widths", "content-width", "effective-part-columns", "is-cart-or-erp-cart-enabled", "add-to-cart-modal-open", "cart-item", "is-widget-cart-enabled", "has-hide-price", "has-more-collections", "total-count", "is-widget", "onCloseCartModal", "onToggleShowRetailPrice", "onToggleShowWholesalePrice", "onToggleShowDiscountPrice"]))
    : (_unref(hasPartCollections))
      ? (_openBlock(), _createBlock(PartCollectionsLayout, {
          key: 1,
          ref_key: "layoutRef",
          ref: layoutRef,
          "owner-id": id.value,
          search: _unref(search),
          "search-results-count": displayedSearchResultsCount.value,
          "is-searching": _unref(isSearching),
          "is-search-mode": _unref(isSearchMode),
          "is-expanding-all": _unref(isExpandingAll),
          collections: visibleCollections.value,
          "column-widths": _unref(columnWidths),
          "content-width": _unref(contentWidth),
          "effective-part-columns": _unref(effectivePartColumns),
          "is-cart-or-erp-cart-enabled": _unref(isCartOrErpCartEnabled),
          "add-to-cart-modal-open": _unref(addToCartModalOpen),
          "cart-item": _unref(cartItem),
          "is-widget-cart-enabled": _unref(isWidgetCartEnabled),
          "get-expanded": getExpanded,
          "has-hide-price": hasHidePrice.value,
          "has-an-expanded-collection": _unref(hasAnExpandedCollection),
          "is-widget": isWidget.value,
          onExpandAll: handleExpandAll,
          onCollapseAll: handleCollapseAll,
          onSearchInput: handleSearchInput,
          onClearSearch: handleClearSearch,
          onScroll: handleScroll,
          onToggleCollection: handleToggleCollection,
          onAddToCart: handleAddToCart,
          onAddAllToCart: handleAddAllToCart,
          onExportParts: exportParts,
          onCartSubmit: handleCartSubmit,
          onCloseCartModal: _unref(closeAddToCartModal),
          onToggleShowRetailPrice: _unref(toggleShowRetailPrice),
          onToggleShowWholesalePrice: _unref(toggleShowWholesalePrice),
          onToggleShowDiscountPrice: _unref(toggleShowDiscountPrice)
        }, null, 8, ["owner-id", "search", "search-results-count", "is-searching", "is-search-mode", "is-expanding-all", "collections", "column-widths", "content-width", "effective-part-columns", "is-cart-or-erp-cart-enabled", "add-to-cart-modal-open", "cart-item", "is-widget-cart-enabled", "has-hide-price", "has-an-expanded-collection", "is-widget", "onCloseCartModal", "onToggleShowRetailPrice", "onToggleShowWholesalePrice", "onToggleShowDiscountPrice"]))
      : (_openBlock(), _createElementBlock("section", _hoisted_1, [
          _createElementVNode("h1", _hoisted_2, _toDisplayString(_ctx.$t('assetsNotFound', { type: _ctx.$tc('partCollection', 2) })), 1)
        ]))
}
}

})